import { Card, CardMedia, Chip, Layout, Typography } from "matsuri-ui"
import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { CleaningStatus } from "@/model/cleaningInfo"
import { useTranslation } from "@/hooks/useTranslation"
import { GuestStayStatus } from "@/model/listingOccupancy"
import { GuestStayStatusChip } from "@/parts/OperationInfo/GuestStayStatusChip"
import { EarlyCheckinChip } from "@/parts/OperationInfo/EarlyCheckinChip"

export interface CleaningItemProps {
    text: string
    imageUrl: string
    status: CleaningStatus
    isNew?: boolean
    checkinAvailable: boolean
    /** 物件のゲスト滞在区分。取得できたときは checkinAvailable の代わりに表示する */
    guestStayStatus?: GuestStayStatus | null
    hasEarlyCheckin?: boolean
    requestAvailable: boolean
    cleaningType?: string
    unreportedWarning?: string
    isImportant?: boolean
    scheduleText?: string
    deadlineText?: string
}

const WorkingChip = styled(Chip)`
    && {
        background-color: #a5d6a7 !important;
    }
`

export const CleaningItem = ({
    isNew = false,
    isImportant = false,
    ...props
}: CleaningItemProps) => {
    const { t } = useTranslation()
    return (
        <Card
            style={{
                margin: "1em 4px"
            }}
        >
            <CardMedia
                src={props.imageUrl}
                style={{
                    backgroundColor: isImportant ? "red" : "inherit"
                }}
                css={css`
                    display: flex;
                    flex-direction: column;
                    min-height: 10rem;
                    /* CardMedia 内部のオーバーレイをカードの高さまで伸ばす */
                    & > div {
                        flex: 1;
                    }
                `}
                overlay
            >
                <div
                    css={css`
                        display: grid;
                        gap: 8px;
                    `}
                >
                    <Layout.Row
                        gap={1}
                        justify={"flex-end"}
                        style={{ minHeight: "28px" }}
                    >
                        {isNew && (
                            <Chip
                                variant={"filled"}
                                color={"secondary"}
                                style={{ fontWeight: "bold" }}
                            >
                                new!
                            </Chip>
                        )}
                        {props.guestStayStatus ? (
                            <GuestStayStatusChip
                                status={props.guestStayStatus}
                            />
                        ) : (
                            props.checkinAvailable && (
                                <Chip
                                    variant={"filled"}
                                    style={{ fontWeight: "bold" }}
                                >
                                    {t("チェックインあり")}
                                </Chip>
                            )
                        )}
                        {props.hasEarlyCheckin && <EarlyCheckinChip />}
                        {props.requestAvailable && (
                            <Chip
                                variant={"filled"}
                                style={{ fontWeight: "bold" }}
                            >
                                {t("お願い事あり")}
                            </Chip>
                        )}
                        {props.status === "started" && (
                            <WorkingChip
                                variant={"filled"}
                                style={{ fontWeight: "bold" }}
                            >
                                {t("清掃中")}
                            </WorkingChip>
                        )}
                        {props.cleaningType && (
                            <Chip
                                color="primary"
                                variant={"filled"}
                                style={{ fontWeight: "bold" }}
                            >
                                {props.cleaningType}
                            </Chip>
                        )}
                    </Layout.Row>
                    <Layout.Column gap={2}>
                        <Typography variant={"h4"} as="p">
                            {props.scheduleText}
                        </Typography>
                        {props.deadlineText && (
                            <Typography
                                variant={"h4"}
                                as="p"
                                style={{
                                    color: "#ffb74d",
                                    fontWeight: 700
                                }}
                            >
                                {props.deadlineText}
                            </Typography>
                        )}
                        <Typography
                            variant={"h4"}
                            as="p"
                            color={"inherit"}
                            bolder
                            css={css`
                                word-break: break-all;
                            `}
                        >
                            {props.text}
                        </Typography>
                    </Layout.Column>
                    <Layout.Column>
                        {props.unreportedWarning && (
                            <div
                                css={css`
                                    width: 100%;
                                    height: 24px;
                                    padding: 4px 12px;
                                    margin-top: 8px;
                                    background: #ffffff;
                                    border-radius: 4px;
                                `}
                            >
                                <Typography
                                    variant="body"
                                    css={css`
                                        font-size: 12px;
                                        font-weight: 700;
                                        line-height: 16px;
                                        color: #fd544b;
                                        text-align: center;
                                        letter-spacing: 0.8;
                                    `}
                                >
                                    {props.unreportedWarning}
                                </Typography>
                            </div>
                        )}
                    </Layout.Column>
                </div>
            </CardMedia>
        </Card>
    )
}
